// Ablage-Regeln (Handoff Dokumente §6.6). Matching ausschließlich über die Backend-Logik
// (_shared/dokument-ablage/regeln.ts, ohne Abhängigkeiten direkt eingebunden) – UI-Test und E-Mail-Analyse entscheiden gleich.

import { baueSignale, findeRegel, type AblageRegel } from '../../supabase/functions/_shared/dokument-ablage/regeln';
import { baueDateiname, dateiEndung, loeseZielpfad } from './dateiname';

export { baueSignale, findeRegel, type AblageRegel };

export interface RegelFormular {
  name: string; prioritaet: string; stichwoerter: string; absender_typ: string; dateityp: string; ziel_pfad_vorlage: string; dokument_wort: string;
}

const liste = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean);

export function regelZuFormular(r: Partial<AblageRegel> & { name?: string | null }): RegelFormular {
  return {
    name: r.name ?? '', prioritaet: r.prioritaet != null ? String(r.prioritaet) : '',
    stichwoerter: (r.stichwoerter ?? []).join(', '), absender_typ: r.absender_typ ?? '', dateityp: (r.dateityp ?? []).join(', '),
    ziel_pfad_vorlage: r.ziel_pfad_vorlage ?? '', dokument_wort: r.dokument_wort ?? '',
  };
}

/** Fehler je Feld; leer = speicherbar. */
export function pruefeRegel(f: RegelFormular): Partial<Record<keyof RegelFormular, string>> {
  const e: Partial<Record<keyof RegelFormular, string>> = {};
  if (!f.name.trim()) e.name = 'Name fehlt';
  if (!/^\d{1,4}$/.test(f.prioritaet.trim())) e.prioritaet = 'Ganze Zahl ab 0';
  if (!f.ziel_pfad_vorlage.trim()) e.ziel_pfad_vorlage = 'Zielordner fehlt';
  else if (f.ziel_pfad_vorlage.trim().startsWith('/') || f.ziel_pfad_vorlage.includes('//')) e.ziel_pfad_vorlage = 'Pfad ohne führenden oder doppelten Schrägstrich';
  if (!f.dokument_wort.trim()) e.dokument_wort = 'Dokument-Wort fehlt';
  return e;
}

/** Formular → Spalten (Stichwörter klein, Dateitypen ohne Punkt). */
export function formularZuRegel(f: RegelFormular) {
  return {
    name: f.name.trim(), prioritaet: Number(f.prioritaet.trim()),
    stichwoerter: liste(f.stichwoerter).map(w => w.toLowerCase()),
    absender_typ: f.absender_typ || null,
    dateityp: liste(f.dateityp).map(d => d.toLowerCase().replace(/^\./, '')),
    ziel_pfad_vorlage: f.ziel_pfad_vorlage.trim().replace(/\/+$/, ''),
    dokument_wort: f.dokument_wort.trim(),
  };
}

export interface RegelTestEingabe { text: string; dateiname: string; absender_typ: string; dateityp: string; objektnummer: string }
export interface RegelTestErgebnis { regel: AblageRegel | null; zielPfad: string | null; dateiname: string | null }

/** Regel testen: erste passende aktive Regel (kleinste Priorität), Vorschau von Zielordner und Dateiname. */
export function testeRegeln(regeln: AblageRegel[], e: RegelTestEingabe, heute: Date): RegelTestErgebnis {
  const endung = (e.dateityp.trim().toLowerCase().replace(/^\./, '')) || dateiEndung(e.dateiname);
  const name = e.dateiname.trim() || (endung ? `datei.${endung}` : 'datei');
  const dateinameMitEndung = dateiEndung(name) ? name : `${name}.${endung}`;
  const s = baueSignale({ absender_typ: e.absender_typ || null, betreff: e.text, dateiname: dateinameMitEndung });
  const regel = findeRegel(regeln, s);
  if (!regel) return { regel: null, zielPfad: null, dateiname: null };
  const basis = e.dateiname.trim().replace(/\.[A-Za-z0-9]+$/, '') || e.text.trim().slice(0, 60) || 'Anhang';
  return {
    regel,
    zielPfad: loeseZielpfad(regel.ziel_pfad_vorlage, heute),
    dateiname: baueDateiname({ objektnummer: e.objektnummer.trim() || null, datum: heute, dokumentWort: regel.dokument_wort, beschreibung: basis, ext: dateiEndung(dateinameMitEndung) }),
  };
}
